import { BUSINESS, SITE_URL } from '../config/business';

export interface FaqEntry {
  question: string;
  answer: string;
}

export type JsonLd = Record<string, unknown>;

/** Référence au LocalBusiness déclaré globalement dans index.html. */
export const BUSINESS_REF = { '@id': `${SITE_URL}/#business` } as const;

export const buildFaqSchema = (faqs: FaqEntry[]): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

interface ServiceSchemaInput {
  name: string;
  serviceType: string;
  path: string;
  description: string;
  cities: string[];
  offers?: { name: string; priceFrom: number }[];
}

export const buildServiceSchema = ({
  name,
  serviceType,
  path,
  description,
  cities,
  offers = [],
}: ServiceSchemaInput): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  serviceType,
  description,
  url: `${SITE_URL}${path}`,
  provider: BUSINESS_REF,
  areaServed: cities.map((city) => ({ '@type': 'City', name: city })),
  ...(offers.length > 0 && {
    offers: offers.map((offer) => ({
      '@type': 'Offer',
      name: offer.name,
      priceCurrency: 'EUR',
      priceSpecification: {
        '@type': 'PriceSpecification',
        minPrice: offer.priceFrom,
        priceCurrency: 'EUR',
      },
      seller: { '@type': 'LocalBusiness', name: BUSINESS.name, ...BUSINESS_REF },
    })),
  }),
});

interface WebsiteOfferSchemaInput {
  name: string;
  serviceType: string;
  path: string;
  audience: string;
  areas: readonly string[];
  setupPrice: number;
  setupDescription: string;
  monthlyPrice: number;
  monthlyDescription: string;
  commitmentMonths: number;
}

/** Service schema.org avec offre de création (prix unique) et abonnement mensuel. */
export const buildWebsiteServiceSchema = ({
  name,
  serviceType,
  path,
  audience,
  areas,
  setupPrice,
  setupDescription,
  monthlyPrice,
  monthlyDescription,
  commitmentMonths,
}: WebsiteOfferSchemaInput): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  serviceType,
  url: `${SITE_URL}${path}`,
  provider: BUSINESS_REF,
  areaServed: areas.map((area) => ({ '@type': 'Place', name: area })),
  audience: { '@type': 'BusinessAudience', audienceType: audience },
  offers: [
    {
      '@type': 'Offer',
      price: setupPrice,
      priceCurrency: 'EUR',
      description: setupDescription,
    },
    {
      '@type': 'Offer',
      priceCurrency: 'EUR',
      description: monthlyDescription,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: monthlyPrice,
        priceCurrency: 'EUR',
        unitText: 'MONTH',
        billingDuration: commitmentMonths,
      },
    },
  ],
});
